import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import GenericMap from "@/assets/Mapsicle Map.webp";
import { scrollToTop, formatFechaHoraEvento } from "@/lib/utilsUI";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import { shareContent } from "@/utils/shareUtil";
import { useModalsContext } from "@/contexts/ModalsContext";
import DropdownMenu from "@/components/DropdownMenu";

/**
 * BoxInfoEvento Component
 * Displays event information including image, description, location, and ticket stats
 *
 * @param {Object} props
 * @param {string} props.uidEvento - Event UID for routing and sharing
 * @param {string} props.uidEmpresa - Company UID for routing
 * @param {string} props.nombre - Event name
 * @param {string} props.fechaHoraInicioEvento - Event start date and time formatted
 * @param {string} props.fechaHoraFinEvento - Event end date and time formatted
 * @param {string} props.zonaHoraria - Event timezone
 * @param {number} props.published - Event publication status (0 = draft, 1 = published)
 * @param {string} props.descripcion - Event description
 * @param {string} props.ubicacion - Event location
 * @param {string} props.direccion - Event address
 * @param {string} props.categoriaEvento - Event category
 * @param {number} props.aforo - Event capacity
 * @param {number} props.totalTicketsPuestosEnVenta - Total tickets available for sale
 * @param {number} props.totalTicketsVendidos - Total tickets sold
 * @param {string} props.imgEvento - Event image URL
 */
const BoxInfoEvento = ({
  uidEvento = "",
  uidEmpresa = "",
  nombre = "",
  fechaHoraInicioEvento = "",
  fechaHoraFinEvento = "",
  zonaHoraria = DEFAULT_TIMEZONE,
  published = 0,
  descripcion = "",
  ubicacion = "",
  direccion = "",
  categoriaEvento = "",
  aforo = 0,
  totalTicketsPuestosEnVenta = 0,
  totalTicketsVendidos = 0,
  imgEvento = null,
}) => {
  // Contextos
  const { openModal } = useModalsContext();

  // Calcular porcentaje de tickets vendidos
  const porcentajeVendido =
    totalTicketsPuestosEnVenta > 0
      ? ((totalTicketsVendidos / totalTicketsPuestosEnVenta) * 100).toFixed(1)
      : 0;

  const formatDatetime = fechaHoraInicioEvento
    ? formatFechaHoraEvento(
        parseUTCDate(fechaHoraInicioEvento, zonaHoraria)?.toJSDate(),
        zonaHoraria,
      )
    : null;

  const formatDatetimeFin = fechaHoraFinEvento
    ? formatFechaHoraEvento(
        parseUTCDate(fechaHoraFinEvento, zonaHoraria)?.toJSDate(),
        zonaHoraria,
      )
    : null;

  // Función para compartir el link del evento
  const handleShare = async () => {
    const eventUrl = `${window.location.origin}/evento/${uidEvento}`;
    
    await shareContent({
      url: eventUrl,
      title: nombre || "Evento",
      text: `¡Mira este evento! ${nombre}`,
      successMessage: "¡Link del evento copiado al portapapeles!",
    });
  };

  // Construir las opciones del menú según el estado del evento
  const menuOptions = [
    {
      type: "none",
      classContent: "bg-aw-bluedark p-1.5",
    },
  ];

  const currentDateTime = new Date();
  const fechaFinEvento = fechaHoraFinEvento 
    ? parseUTCDate(fechaHoraFinEvento, zonaHoraria)?.toJSDate() 
    : null;

  // Evento finalizado (publicado y fecha de fin pasada)
  if (published === 1 && fechaFinEvento && currentDateTime > fechaFinEvento) {
    menuOptions.push(
      {
        type: "link",
        label: "Ver Evento",
        icon: "fa-solid fa-eye",
        to: `/evento/${uidEvento}`,
        className: "text-white hover:bg-aw-green/20",
      }
    );
  } 
  // Evento publicado (activo)
  else if (published === 1) {
    menuOptions.push(
      {
        type: "link",
        label: "Ver Evento",
        icon: "fa-solid fa-eye",
        to: `/evento/${uidEvento}`,
        className: "text-white hover:bg-aw-green/20",
      },
      {
        type: "link",
        label: "Ajustar Opciones",
        icon: "fa-solid fa-ticket",
        to: `/empresa/${uidEmpresa}/evento/${uidEvento}`,
        className: "text-white hover:bg-aw-green/20",
      },
      {
        type: "link",
        label: "Dashboard Evento",
        icon: "fa-solid fa-gear",
        to: `/empresa/${uidEmpresa}/evento/${uidEvento}/dashboard`,
        className: "text-white hover:bg-aw-green/20",
      },
      // { divider: true },
      // {
      //   type: "button",
      //   label: "Cancelar Evento",
      //   icon: "fa-solid fa-xmark-circle",
      //   onClick: () => openModal("cancelar"),
      //   className: "text-red-600 hover:bg-red-600/20",
      // },
      { divider: true },
      {
        type: "button",
        label: "Compartir Evento",
        icon: "fa-solid fa-share",
        onClick: handleShare,
        className: "text-white hover:bg-aw-green/20",
      }
    );
  }

  return (
    <div className="w-full flex flex-col lg:flex-row items-center bg-aw-bluedark-75 inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green rounded-2xl text-sm text-gray-300 p-4 my-4 gap-6 relative">
      {/* Menú de Opciones en la esquina superior derecha */}
      {published === 1 && (
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          {/* Botón para compartir el link del evento */}
          <button
            onClick={handleShare}
            className="w-8 h-8 flex items-center justify-center bg-aw-bluedark-50 text-white font-semibold rounded-full hover:bg-aw-green hover:text-aw-bluedark transition border-2 border-aw-bluegray hover:border-aw-green active:border-aw-green"
            title="Compartir evento"
          >
            <FontAwesomeIcon icon="fa-solid fa-share-nodes" />
          </button>
          
          {/* Menú desplegable de opciones */}
          <DropdownMenu
            trigger={
              <button 
                className="w-8 h-8 flex items-center justify-center bg-aw-bluedark-50 text-white font-semibold rounded-full hover:bg-aw-green hover:text-aw-bluedark transition border-2 border-aw-bluegray hover:border-aw-green active:border-aw-green"
                title="Opciones del evento"
              >
                <FontAwesomeIcon icon="fa-solid fa-ellipsis" />
              </button>
            }
            options={menuOptions}
            direction="down"
            align="end"
            classContent={menuOptions[0]?.classContent || "bg-white"}
          />
        </div>
      )}
      
      {/* Imagen del Evento */}
      <div className="w-full lg:w-3/12">
        <img
          src={imgEvento}
          alt={nombre}
          className="rounded-2xl mx-auto w-full object-cover"
        />
      </div>

      {/* Información del Evento */}
      <div className="w-full lg:w-6/12 text-center lg:text-left">
        {/* Titulo */}
        <h3 className="text-4xl font-bold text-aw-green">{nombre}</h3>

        {/* Fecha y Horario */}
        <p className="text-base">
          <span>Inicio:</span> {formatDatetime}
        </p>
        {formatDatetimeFin && (
          <p className="text-base">
            <span>Fin:</span> {formatDatetimeFin}
          </p>
        )}

        {/* Descripción */}
        <h3 className="text-base font-semibold mt-2">
          Descripción del evento:
        </h3>
        <p className="text-sm text-gray-200 mb-2 max-h-28 overflow-y-auto pr-2 scrollbar-box">
          {descripcion}
        </p>

        {/* Aforo */}
        <h3 className="text-xl font-bold text-white">
          Aforo para <span className="text-aw-green">{aforo}</span> personas
        </h3>

        {/* Tickets asignados vs vendidos */}
        <h3 className="text-xl font-bold text-white">
          Tickets vendidos:{" "}
          <span className="text-cyan-400 font-medium">
            {totalTicketsVendidos}
          </span>{" "}
          /{" "}
          <span className="text-aw-green font-medium">
            {totalTicketsPuestosEnVenta}
          </span>{" "}
          <span className="text-sm text-gray-400">({porcentajeVendido}%)</span>
        </h3>
      </div>

      {/* Ubicación y keywords */}
      <div className="w-full lg:w-3/12">
        {/* Ubicación */}
        <div className="relative w-full h-28 mb-10">
          <h3 className="text-lg font-semibold">Ubicación</h3>
          <div className="absolute w-full h-full bg-aw-bluedark-50 flex flex-col gap-y-2 items-center justify-center px-4">
            <span className="text-xs text-white line-clamp-1">
              <span className="font-medium">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="text-red-500"
                />{" "}
                {direccion}
              </span>
            </span>
            {/* Botones de mapa */}
            {ubicacion && (
              <div className="flex">
                <a
                  href={ubicacion}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-aw-bluedark text-white px-3 py-1 rounded-full border border-aw-green hover:bg-aw-green hover:text-aw-bluedark text-xs duration-300"
                >
                  Google Maps
                </a>
              </div>
            )}
          </div>
          <img
            src={GenericMap}
            alt="Mapa de ubicación"
            className="w-full h-full object-cover rounded-lg"
          />
        </div>

        {/* Palabras clave */}
        <div className="w-full">
          <h4 className="font-semibold text-base text-white text-left">
            Categoría de Evento
          </h4>
          <div className="flex flex-wrap gap-2 mt-2">
            {categoriaEvento ? (
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-aw-green text-aw-bluedark">
                {categoriaEvento}
              </span>
            ) : (
              <span className="text-xs text-gray-400">Sin Categoría</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxInfoEvento;
